import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type { DeviceId, DeviceInstanceId } from '../domain/ids';
import type {
  DeviceInstance,
  DeviceInstanceDetail,
  DeviceInstanceSummary,
} from '../domain/deviceInstance';
import type {
  DeviceInstanceCreatePayload,
  DeviceInstanceMovePayload,
} from '../domain/deviceInstance.rules';

const DeviceInstanceModelRefDtoSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  manufacturer: z.string().nullish(),
  model_identifier: z.string().nullish(),
  category_name: z.string().nullish(),
});

const DeviceInstanceClientRefDtoSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
});

const DeviceInstanceRackRefDtoSchema = z.object({
  id: z.string().nullish(),
  code: z.string().nullish(),
  name: z.string().nullish(),
});

const DeviceInstanceNamedRefDtoSchema = z.object({
  id: z.string().nullish(),
  code: z.string().nullish(),
  name: z.string().nullish(),
});

const DeviceInstancePreviousLocationDtoSchema = z.object({
  rack_id: z.string().nullish(),
  room_id: z.string().nullish(),
  datacenter_id: z.string().nullish(),
  client_id: z.string().nullish(),
  units: z.array(z.number()).nullish(),
  saved_at: z.string().nullish(),
});

const DeviceInstanceNavigationDtoSchema = z.object({
  client: DeviceInstanceClientRefDtoSchema.nullish(),
  datacenter: DeviceInstanceNamedRefDtoSchema.nullish(),
  room: DeviceInstanceNamedRefDtoSchema.nullish(),
  rack: DeviceInstanceRackRefDtoSchema.nullish(),
  device_instance: z.object({ id: z.string(), asset_tag: z.string().nullish() }).nullish(),
});

const DeviceInstanceSummaryDtoSchema = z.object({
  id: z.string(),
  device_model: DeviceInstanceModelRefDtoSchema,
  serial_number: z.string().nullish(),
  management_ip: z.string().nullish(),
  usage_type: z.string().nullish(),
  operational_status: z.string(),
  ownership_type: z.enum(['Kyndryl', 'Client']),
  owning_client_id: z.string().nullish(),
  owning_enterprise_id: z.string().nullish(),
  client: DeviceInstanceClientRefDtoSchema,
  company_id: z.string().nullish(),
  rack: DeviceInstanceRackRefDtoSchema,
  rack_units: z.array(z.number()),
});

const DeviceInstanceDetailDtoSchema = DeviceInstanceSummaryDtoSchema.extend({
  navigation: DeviceInstanceNavigationDtoSchema,
  previous_location: DeviceInstancePreviousLocationDtoSchema.nullish(),
  notes: z.string().nullish(),
  deployment_metadata: z.record(z.string(), z.unknown()).nullish(),
  virtual_machines: z
    .object({ available: z.boolean().nullish(), count: z.number(), path: z.string().nullish() })
    .nullish(),
  version: z.number(),
});

const DeviceInstanceMutationAckDtoSchema = z.object({ id: z.string() });

/**
 * Campos editables de una instancia (UC-15, `DeviceInstanceDetailPage.jsx:110-116`). No hay un
 * tipo de payload dedicado en `domain/deviceInstance.ts` (T4) porque esta normalización no
 * requiere `parseInt`/`parseFloat` (a diferencia de `DeviceInstanceCreatePayload`/
 * `DeviceInstanceMovePayload`, T12) — se deriva aquí de `DeviceInstance` en vez de declarar un tipo
 * nuevo en domain/ (T21 no modifica domain/).
 */
export type DeviceInstanceUpdateFields = Partial<
  Pick<DeviceInstance, 'serial' | 'managementIp' | 'deploymentNotes' | 'status' | 'usageType'>
>;

export type DeviceInstanceUpdatePayload = DeviceInstanceUpdateFields & { version: number };

function toSummaryDomain(
  dto: z.infer<typeof DeviceInstanceSummaryDtoSchema>
): DeviceInstanceSummary {
  const deviceModelRef = {
    id: dto.device_model.id ? (dto.device_model.id as DeviceId) : undefined,
    name: dto.device_model.name ?? undefined,
    manufacturer: dto.device_model.manufacturer ?? undefined,
    modelIdentifier: dto.device_model.model_identifier ?? undefined,
    categoryName: dto.device_model.category_name ?? undefined,
  };
  const client = {
    id: dto.client.id ?? undefined,
    name: dto.client.name ?? undefined,
  };
  const rack = {
    id: dto.rack.id ?? undefined,
    code: dto.rack.code ?? undefined,
    name: dto.rack.name ?? undefined,
  };

  return {
    id: dto.id as DeviceInstanceId,
    deviceId: (dto.device_model.id ?? '') as DeviceId,
    deviceModel: dto.device_model.name ?? undefined,
    deviceBrand: dto.device_model.manufacturer ?? undefined,
    deviceType: dto.device_model.category_name ?? undefined,
    model: dto.device_model.model_identifier ?? undefined,
    serial: dto.serial_number ?? undefined,
    managementIp: dto.management_ip ?? undefined,
    usageType: dto.usage_type ?? undefined,
    status: dto.operational_status,
    ownerType: dto.ownership_type,
    ownerClientId: dto.owning_client_id ?? undefined,
    ownerEnterpriseId: dto.owning_enterprise_id ?? undefined,
    clientId: dto.client.id ?? undefined,
    clientName: dto.client.name ?? undefined,
    clientCompanyId: dto.company_id ?? undefined,
    rackId: dto.rack.id ?? undefined,
    rackName: dto.rack.name ?? undefined,
    rackUnits: dto.rack_units,
    deviceModelRef,
    client,
    rack,
  };
}

function toDetailDomain(
  dto: z.infer<typeof DeviceInstanceDetailDtoSchema>
): DeviceInstanceDetail {
  const previousLocation = dto.previous_location
    ? {
        rackId: dto.previous_location.rack_id ?? undefined,
        roomId: dto.previous_location.room_id ?? undefined,
        datacenterId: dto.previous_location.datacenter_id ?? undefined,
        clientId: dto.previous_location.client_id ?? undefined,
        rackUnits: dto.previous_location.units ?? undefined,
        savedAt: dto.previous_location.saved_at ?? undefined,
      }
    : undefined;
  const navigation = {
    client: dto.navigation.client
      ? {
          id: dto.navigation.client.id ?? undefined,
          name: dto.navigation.client.name ?? undefined,
        }
      : undefined,
    datacenter: dto.navigation.datacenter
      ? {
          id: dto.navigation.datacenter.id ?? undefined,
          code: dto.navigation.datacenter.code ?? undefined,
          name: dto.navigation.datacenter.name ?? undefined,
        }
      : undefined,
    room: dto.navigation.room
      ? {
          id: dto.navigation.room.id ?? undefined,
          code: dto.navigation.room.code ?? undefined,
          name: dto.navigation.room.name ?? undefined,
        }
      : undefined,
    rack: dto.navigation.rack
      ? {
          id: dto.navigation.rack.id ?? undefined,
          code: dto.navigation.rack.code ?? undefined,
          name: dto.navigation.rack.name ?? undefined,
        }
      : undefined,
  };

  return {
    ...toSummaryDomain(dto),
    salaId: dto.navigation.room?.id ?? undefined,
    salaName: dto.navigation.room?.name ?? undefined,
    datacenterName: dto.navigation.datacenter?.name ?? undefined,
    previousRackId: previousLocation?.rackId,
    previousSalaId: previousLocation?.roomId,
    previousDatacenterId: previousLocation?.datacenterId,
    previousRackUnits: previousLocation?.rackUnits,
    lastLocationSavedAt: previousLocation?.savedAt,
    deploymentNotes: dto.notes ?? undefined,
    vmCount: dto.virtual_machines?.count ?? undefined,
    version: dto.version,
    navigation,
    previousLocation,
    notes: dto.notes ?? undefined,
    deploymentMetadata: dto.deployment_metadata ?? undefined,
  };
}

// Nombres de campo del DTO alineados con `CreateDeviceInstanceRequestDto` (specs/api/
// core-manage-api-v1.json) — huérfano del documento OpenAPI (no enlazado desde el 201 de la
// operación, ver memoria repo `api-v1-orphaned-schemas`), pero es el shape real que valida el
// backend: `ownerType`→`ownershipType`, `deploymentNotes`→`notes`, `ownerClientId`→
// `owningClientId`, `ownerEnterpriseId`→`owningEnterpriseId`, `clientCompanyId`→`companyId`.
function toCreateDto(payload: DeviceInstanceCreatePayload) {
  return {
    deviceModelId: payload.deviceId,
    clientId: payload.clientId,
    serialNumber: payload.serial,
    rackId: payload.rackId,
    rackUnits: payload.rackUnits,
    usageType: payload.usageType,
    ownershipType: payload.ownerType,
    managementIp: payload.managementIp,
    notes: payload.deploymentNotes,
    owningClientId: payload.ownerClientId,
    owningEnterpriseId: payload.ownerEnterpriseId,
    companyId: payload.clientCompanyId,
  };
}

function toUpdateDto(payload: DeviceInstanceUpdatePayload) {
  return {
    serialNumber: payload.serial,
    managementIp: payload.managementIp,
    notes: payload.deploymentNotes,
    operationalStatus: payload.status,
    usageType: payload.usageType,
    version: payload.version,
  };
}

function toMoveDto(payload: DeviceInstanceMovePayload) {
  return {
    destinationRackId: payload.rackId,
    moveReason: payload.moveReason,
    destinationRackUnits: payload.rackUnits,
  };
}

function parseDetail(data: unknown): DeviceInstanceDetail | undefined {
  const result = DeviceInstanceDetailDtoSchema.safeParse(toSnakeCaseKeys(data));
  return result.success ? toDetailDomain(result.data) : undefined;
}

async function fetchDetail(id: DeviceInstanceId): Promise<DeviceInstanceDetail | null> {
  const { data } = await apiClient.get(`/device-instances/${id}`);
  if (!data) return null;
  return toDetailDomain(DeviceInstanceDetailDtoSchema.parse(toSnakeCaseKeys(data)));
}

async function resolveMutationDetail(
  data: unknown,
  fallbackId?: DeviceInstanceId
): Promise<DeviceInstanceDetail> {
  const detail = parseDetail(data);
  if (detail) return detail;

  const id =
    fallbackId ??
    (DeviceInstanceMutationAckDtoSchema.parse(toSnakeCaseKeys(data)).id as DeviceInstanceId);
  const refetched = await fetchDetail(id);
  if (!refetched) throw new Error(`Device instance ${id} was not found after mutation`);
  return refetched;
}

export const deviceInstanceRepository = {
  async getById(id: DeviceInstanceId): Promise<DeviceInstanceDetail | null> {
    return fetchDetail(id);
  },

  async create(payload: DeviceInstanceCreatePayload): Promise<DeviceInstanceDetail> {
    const { data } = await apiClient.post('/device-instances', toCreateDto(payload));
    return resolveMutationDetail(data);
  },

  async update(
    id: DeviceInstanceId,
    payload: DeviceInstanceUpdatePayload
  ): Promise<DeviceInstanceDetail> {
    const { data } = await apiClient.patch(`/device-instances/${id}`, toUpdateDto(payload));
    return resolveMutationDetail(data, id);
  },

  async move(id: DeviceInstanceId, payload: DeviceInstanceMovePayload): Promise<DeviceInstanceDetail> {
    const { data } = await apiClient.post(`/device-instances/${id}/move`, toMoveDto(payload));
    return resolveMutationDetail(data, id);
  },

  // v1 no filtra el listado por modelo: se trae la colección y se filtra en memoria (UC-14, para
  // detectar qué otros clientes tienen instancias del mismo modelo del catálogo).
  async getSharedClients(deviceId: DeviceId): Promise<DeviceInstanceSummary[]> {
    const { data } = await apiClient.get('/device-instances');
    return z
      .array(DeviceInstanceSummaryDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toSummaryDomain)
      .filter((i) => i.deviceId === deviceId);
  },

  // Deja pasar el error del backend tal cual — el mapeo a mensaje legible es trabajo de
  // application/ (UC-13: t('deviceInstances.deleteError')).
  async remove(id: DeviceInstanceId): Promise<void> {
    await apiClient.delete(`/device-instances/${id}`);
  },
};
